import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ThreadPost, ThreadSummary, fetchThread, lockThread, postReply, removeReply, removeThread } from '../../api/site';
import { useSiteViewer } from './SiteLayout';
import { timeAgo } from './ForumPage';

/**
 * One thread: the opening post, its replies, and the reply composer. A reply
 * from Form-Magic staff sits on the accent wash as well as carrying the badge,
 * so the answer stands out in a long thread (ABC6-06 draws the staff answer
 * apart from the rest).
 */
export default function ThreadPage() {
  const { id } = useParams();
  const threadId = parseInt(id || '', 10);
  const viewer = useSiteViewer();

  const [thread, setThread] = useState<ThreadSummary | null>(null);
  const [posts, setPosts] = useState<ThreadPost[]>([]);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState('');
  // Form-Magic staff only: the server says so, and checks again on each action.
  const [canModerate, setCanModerate] = useState(false);
  const [modError, setModError] = useState('');
  const navigate = useNavigate();

  const load = () =>
    fetchThread(threadId)
      .then((r) => { setThread(r.thread); setPosts(r.posts); setCanModerate(r.canModerate === true); })
      .catch((e) => setError(e.message || 'Could not load the thread'));

  useEffect(() => {
    if (Number.isFinite(threadId)) load();
    else setError('Thread not found');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPostError(''); setBusy(true);
    try {
      await postReply(threadId, reply.trim());
      setReply('');
      await load();
    } catch (err: any) {
      setPostError(err.message || 'Could not post the reply');
    } finally {
      setBusy(false);
    }
  }

  /** One moderation action, then the thread as it now stands. */
  async function moderate(action: () => Promise<unknown>, after: () => unknown = load) {
    setModError('');
    try { await action(); await after(); } catch (err: any) { setModError(err.message || 'That did not work'); }
  }

  if (error) {
    return (
      <div className="thread-page">
        <div className="comm-err">{error}</div>
        <p className="comm-back"><Link to="/community">← Back to the forum</Link></p>
      </div>
    );
  }
  if (!thread) return <div className="site-empty">Loading…</div>;

  return (
    <div className="thread-page">
      <p className="comm-back"><Link to="/community">← All threads</Link></p>
      <h2 className="thread-title">
        {thread.title}
        {thread.pinned && <span className="fs-pill info">Pinned</span>}
        {thread.locked && <span className="fs-pill neutral">Locked</span>}
      </h2>
      {canModerate && (
        <div className="comm-mod">
          <button type="button" className="fs-btn neutral sm" onClick={() => void moderate(() => lockThread(threadId, !thread.locked))}>
            {thread.locked ? 'Reopen thread' : 'Lock thread'}
          </button>
          <button
            type="button" className="fs-btn danger sm"
            onClick={() => {
              if (window.confirm('Remove this thread and all of its replies? This cannot be undone.')) {
                void moderate(() => removeThread(threadId), () => navigate('/community'));
              }
            }}
          >
            Remove thread
          </button>
          {modError && <span className="comm-err">{modError}</span>}
        </div>
      )}

      <article className={`post-card op${thread.author_is_staff ? ' staff' : ''}`}>
        <div className="who">
          <span className="name">{thread.author_name}</span>
          {thread.author_is_staff && <span className="fs-pill identity">Form-Magic team</span>}
          <span className="when">{timeAgo(thread.created_at)}</span>
        </div>
        <div className="body">{thread.body}</div>
      </article>

      {posts.map((p) => (
        <article key={p.id} className={`post-card${p.author_is_staff ? ' staff' : ''}`}>
          <div className="who">
            <span className="name">{p.author_name}</span>
            {p.author_is_staff && <span className="fs-pill identity">Form-Magic team</span>}
            <span className="when">{timeAgo(p.created_at)}</span>
            {canModerate && (
              <button
                type="button" className="fs-btn neutral sm comm-mod-remove"
                onClick={() => { if (window.confirm(`Remove this reply by ${p.author_name}?`)) void moderate(() => removeReply(p.id)); }}
              >
                Remove
              </button>
            )}
          </div>
          <div className="body">{p.body}</div>
        </article>
      ))}

      {thread.locked ? (
        <div className="comm-signin">This thread is locked — it stays readable, but no longer accepts replies.</div>
      ) : viewer ? (
        <form className="comm-form" onSubmit={submit}>
          <h3>Reply</h3>
          {postError && <div className="comm-err">{postError}</div>}
          <div className="fs-field">
            <label htmlFor="thread-reply" className="fs-sr-only">Your reply</label>
            <textarea id="thread-reply" value={reply} rows={5} onChange={(e) => setReply(e.target.value)}
              placeholder="Answers from customers are as welcome as ours — share what worked." />
          </div>
          <div>
            <button className="fs-btn primary" disabled={busy || !reply.trim()}>
              {busy ? 'Posting…' : 'Post reply'}
            </button>
          </div>
        </form>
      ) : viewer === null ? (
        <div className="comm-signin">
          <Link to="/login">Sign in</Link> or <Link to="/pricing">create an account</Link> to reply.
        </div>
      ) : null}
    </div>
  );
}
